import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { UserCardAppCard } from "@-/dev.improve.haniverse.lib.cards.app-card";
import {
  type CardIdentityKey,
  type CardReviewSettings,
  cardContentDrillType,
  cardContentIdentityKey,
  cardContentSubject,
  isCardContentKindReviewEligible,
  resolveAppCardContent,
} from "@-/dev.improve.haniverse.lib.cards.card-content";

export type StudyCard = Awaited<
  ReturnType<ReturnType<ActorDatabase>["query"]["userCard"]["findMany"]>
>[number];
export type FsrsCard = Awaited<
  ReturnType<ReturnType<ActorDatabase>["query"]["fsrsCard"]["findMany"]>
>[number];

/**
 * One in-study card, joined across actor (`studyCard` + its `fsrsCard`) and content
 * (`grapheme` + `drillType`). This is the unit both the Learn-index navigator and
 * `resolveLearnCardDestination` rank to pick the next drill — neither filters by `due` here so each can
 * apply its own gating (the navigator filters as-is; the resolver first overrides the
 * just-reviewed card's `due`).
 *
 * `identityKey` is what the card IS (its subject plus drill type, see `CardIdentityKey`), so the
 * fsrs-resolution lookup matches a route's `(grapheme, drillType)` by building the same key.
 */
export type StudyCardData = {
  drillType: DrillType;
  fsrsCard: FsrsCard;
  grapheme: string;
  identityKey: CardIdentityKey;
  studyCard: StudyCard;
};

/**
 * Join in-study cards to their content and scheduling rows into {@link StudyCardData}.
 *
 * Used by the subject-scoped list so review resolution applies the SAME rules: a card
 * whose content row is missing from the active content database, whose kind the settings gate
 * out, or whose FSRS row is absent is dropped — the rating footer and the scheduler must agree on
 * the rateable set.
 */
export function buildStudyCardDataList({
  appCardByUserCardId,
  cardReviewSettings,
  fsrsCardById,
  userCardList,
}: {
  appCardByUserCardId: ReadonlyMap<string, UserCardAppCard>;
  cardReviewSettings: CardReviewSettings;
  fsrsCardById: ReadonlyMap<string, FsrsCard>;
  userCardList: readonly StudyCard[];
}): StudyCardData[] {
  return userCardList.flatMap((studyCard): StudyCardData[] => {
    const userCardAppCard = appCardByUserCardId.get(studyCard.id);
    if (userCardAppCard === undefined) {
      return [];
    }
    const cardContent = resolveAppCardContent(userCardAppCard);
    // A quiz is answered on its own card, never drilled, so it has no drill data to list.
    if (cardContent === null || cardContent.kind === "quiz") {
      return [];
    }
    // Gate on the same Pinyin-flashcards rule the next-due scheduler applies, so the card
    // the scheduler serves and the card the rating footer can rate are always the same
    // set. Without this, a pronunciation card served (or deep-linked) while the setting is
    // off would still resolve here and paint rating buttons the scheduler considers absent.
    if (
      !isCardContentKindReviewEligible({
        cardContentKind: cardContent.kind,
        settings: cardReviewSettings,
      })
    ) {
      return [];
    }
    const fsrsCardId = studyCard.fsrsCardId;
    if (fsrsCardId === null) {
      return [];
    }
    const fsrsCard = fsrsCardById.get(fsrsCardId);
    if (fsrsCard === undefined) {
      return [];
    }
    return [
      {
        drillType: cardContentDrillType(cardContent),
        fsrsCard,
        grapheme: cardContentSubject(cardContent).text,
        identityKey: cardContentIdentityKey(cardContent),
        studyCard,
      },
    ];
  });
}
